import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, IconButton, Typography, getContrastRatio } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CustomTooltip from '../CustomTooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import '@fontsource/rubik';
import { generateLighterContrastColor, getFileType, removeExtension, sizeConverter, timeStampToNumberedDateString } from '../../utils/commonServices';
import CustomAlert from '../../pages/events/components/CustomAlert';
import useCustomTheme from '../../theme/useCustomTheme';
import { MOBILE, PREVIEW_STAGE_ASPECT } from './mediaConstants';

// The dialog is never wider than this, and never so tall that its 16:9 stage is
// pushed off a short screen - DIALOG_CHROME_HEIGHT is the title, counter and
// padding that sit around the stage.
const MAX_STAGE_WIDTH = 1200;
const DIALOG_CHROME_HEIGHT = 225;

// The stage never changes size from one file to the next. It is a fixed 16:9
// black box; whatever is shown inside is fitted to it with `object-fit: contain`,
// so a portrait clip, a 4:3 photo and a 21:9 video all letterbox against the
// same frame instead of resizing the dialog around them.
const stageSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: PREVIEW_STAGE_ASPECT,
  backgroundColor: '#000000',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// Fills the stage without cropping or stretching, whatever the source's shape.
const fittedMediaSx = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  backgroundColor: '#000000',
  outline: 'none',
};

// The same actions the file's row carries, so a reviewer never has to close the
// dialog to act on what they are looking at.
const actionButtonSx = {
  color: '#08231B',
  '& .MuiSvgIcon-root': { fontSize: { xs: '16px', md: '18px', lg: '20px', xl: '26px' } },
  [MOBILE]: { width: '44px', height: '44px' },
};

// Close is a dismissal, not an action on the file, so its glyph is set smaller
// than the action icons and tucked into the corner with no padding of its own.
// On a phone the glyph stays smaller while the padding takes the button out to
// the same 44px tap target the action icons get - a dismissal should be no
// harder to hit than the things it sits beside, only quieter.
const closeButtonSx = {
  color: '#08231B',
  padding: '2px',
  '& .MuiSvgIcon-root': { fontSize: { xs: '17px', md: '18px', lg: '20px', xl: '26px' } },
  [MOBILE]: { width: '44px', height: '44px', padding: '10px', '& .MuiSvgIcon-root': { fontSize: '24px' } },
};

const navButtonSx = {
  color: '#FFFFFF',
  backgroundColor: 'rgba(8, 35, 27, 0.55)',
  '&:hover': { backgroundColor: 'rgba(8, 35, 27, 0.75)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', backgroundColor: 'rgba(8, 35, 27, 0.25)' },
  '& .MuiSvgIcon-root': { fontSize: { xs: '20px', md: '24px', xl: '32px' } },
  [MOBILE]: { width: '44px', height: '44px' },
};

/**
 * Quick preview for the media list: the selected file at full size without
 * leaving the browser, plus next/previous so a reviewer can walk a project's
 * uploads in one sitting rather than opening and closing each file, and the same
 * download / favourite / delete actions its row carries.
 *
 * MUI's Dialog already traps focus and closes on Escape; the arrow keys are
 * wired up here on the same principle.
 */
const MediaQuickPreview = (props) => {
  const {
    open,
    files = [],
    index = 0,
    onClose,
    onNavigate,
    canDownload = false,
    onDownload,
    canFavorite = false,
    onFavorite,
    canRemove = false,
    removeMode = 'delete',
    onRemove,
    actionsDisabled = false,
    isRemoving = false,
    isFavoriting = false,
  } = props;

  // With the other hooks, above the early returns further down - the delete
  // confirmation needs the brand colour, and a hook called beside it would run
  // conditionally.
  const [theme] = useCustomTheme();

  const file = files[index];
  // The listing APIs already say what a file is. `getFileType` only knows a
  // handful of extensions - three for video - so deriving the type from the
  // name sent everything else (m4v, avi, mkv, webm, mpg, heic, gif, flac...)
  // to the iframe branch, where nothing plays. The server's answer wins; the
  // filename is the fallback for payloads that carry no type, and for
  // documents, which have no player either way.
  const serverType = ['video', 'image', 'audio'].includes(file?.content_type) ? file.content_type : null;
  const fileType = serverType || getFileType(file?.name || '');
  const url = file?.video_url;
  // Deleting is permanent and there is no undo anywhere in the product, so the
  // button inside the dialog asks first. It sits beside Download and the paging
  // arrows on the file the user is currently looking at, which is exactly where
  // a mis-click while paging at speed would land.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // Focus lands on Close when the dialog opens, so Escape, Tab and the arrow
  // keys are all reachable without a pointer.
  const closeButtonRef = useRef(null);
  // Remounts the player on navigation so the next file starts from its own
  // beginning instead of inheriting the previous one's playhead.
  const [mediaKey, setMediaKey] = useState(0);
  // Several formats these APIs accept are ones no browser decodes - .avi,
  // .mkv, .mxf and .wmv among the videos, raw stills among the images. Rather
  // than keep a codec table that will drift, the element is given its chance
  // and the failure is caught: whatever plays, plays, and whatever does not
  // says so instead of sitting there black.
  const [mediaFailed, setMediaFailed] = useState(false);

  const hasPrevious = index > 0;
  const hasNext = index < files.length - 1;

  const goTo = useCallback(
    (nextIndex) => {
      if (nextIndex < 0 || nextIndex >= files.length) return;
      setMediaKey((key) => key + 1);
      if (onNavigate) onNavigate(nextIndex);
    },
    [files.length, onNavigate]
  );

  // A confirmation belongs to the file it was raised on. Paging away from that
  // file, or closing the dialog, must not leave it standing over a different one.
  useEffect(() => {
    setConfirmingRemove(false);
    setMediaFailed(false);
  }, [index, open]);

  useEffect(() => {
    if (!open) return undefined;
    const keyHandler = (event) => {
      if (event.key === 'ArrowRight') goTo(index + 1);
      if (event.key === 'ArrowLeft') goTo(index - 1);
    };
    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  }, [open, index, goTo]);

  if (!open || !file) return null;

  const renderMedia = () => {
    if (mediaFailed) {
      return (
        <Box className="flex col j-center a-center" data-testid="quick-preview-unavailable" sx={{ height: '100%', width: '100%', px: 3, gap: 1 }}>
          <Typography
            sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#FFFFFF', textAlign: 'center', fontSize: { xs: '11px', md: '13px', xl: '18px' } }}
          >
            This file can't be played in the browser
          </Typography>
          <Typography
            sx={{ fontFamily: 'Rubik', color: '#c7d2ce', textAlign: 'center', fontSize: { xs: '9px', md: '11px', xl: '15px' } }}
          >
            {canDownload
              ? 'Download it to open in your own player.'
              : 'Its format is one browsers cannot open. Your editor can still use it.'}
          </Typography>
        </Box>
      );
    }

    if (fileType === 'video') {
      return (
        <Box
          component="video"
          key={mediaKey}
          src={url}
          controls
          autoPlay={false}
          preload="metadata"
          playsInline
          data-testid="quick-preview-video"
          aria-label={`Video preview of ${file?.name}`}
          onError={() => setMediaFailed(true)}
          sx={fittedMediaSx}
        />
      );
    }

    if (fileType === 'image') {
      return (
        <Box
          component="img"
          key={mediaKey}
          src={url}
          alt={file?.name}
          data-testid="quick-preview-image"
          onError={() => setMediaFailed(true)}
          sx={fittedMediaSx}
        />
      );
    }

    if (fileType === 'audio') {
      return (
        <Box
          component="audio"
          key={mediaKey}
          src={url}
          controls
          preload="metadata"
          data-testid="quick-preview-audio"
          aria-label={`Audio preview of ${file?.name}`}
          onError={() => setMediaFailed(true)}
          sx={{ width: '80%' }}
        />
      );
    }

    return (
      <Box
        component="iframe"
        key={mediaKey}
        src={url}
        title={`Preview of ${file?.name}`}
        data-testid="quick-preview-frame"
        sx={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#FFFFFF' }}
      />
    );
  };

  const removing = removeMode === 'unlink' ? 'Remove' : 'Delete';

  // The brand colour where it is legible against #08231B, a lightened variant
  // where it is not. The same contrast-checked accent every other delete
  // confirmation in the app uses, which is what keeps the dark button readable.
  const accent = theme.palette.primary.main === ('#00d744' || '#00D744')
    ? '#00d744'
    : getContrastRatio('#08231B', theme.palette.primary.main) > 4.5
      ? `${theme.palette.primary.main}`
      : generateLighterContrastColor(theme.palette.primary.main);

  // One size for both buttons, matching the revision-content and revision-log
  // confirmations so the app has a single delete dialog rather than three.
  const confirmButtonSx = {
    fontFamily: 'Rubik',
    fontWeight: 600,
    fontSize: { xs: '9px', sm: '9px', md: '9px', lg: '11px', xl: '16px' },
    width: { xs: '86px', sm: '86px', md: '86px', lg: '96px', xl: '139px' },
    paddingY: 0.4,
    minWidth: '110px !important',
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="media-quick-preview-title"
      maxWidth={false}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(2px)' } } }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          m: { xs: 1, md: 3 },
          // One width for every file. It adapts to the viewport - narrow
          // windows and short windows both shrink it - but never to the file,
          // so paging from a 4K landscape clip to a portrait phone photo leaves
          // the dialog, the player and the image frame exactly where they were.
          width: `min(${MAX_STAGE_WIDTH}px, calc(100vw - 48px), calc((100vh - ${DIALOG_CHROME_HEIGHT}px) * 16 / 9))`,
          maxWidth: 'none',
          [MOBILE]: { m: 0, borderRadius: 0, width: '100%', maxWidth: '100%', maxHeight: '100%', height: '100%' },
        },
      }}
    >
      {/* Less top padding than the sides: the close button is the first thing in
          here and wants to sit near the top of the dialog, not inset like the
          content below it. */}
      <DialogContent
        sx={{
          px: { xs: 1.5, md: 2.5 },
          pb: { xs: 1.5, md: 2.5 },
          pt: { xs: 0.75, md: 1 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Close sits on its own row above the actions that act on the file, so
            dismissing the dialog can never be mistaken for one of them, or hit
            by accident while reaching for one. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            // Pulled into the corner, and the negative bottom cancels the gap
            // DialogContent puts between its rows.
            marginRight: '-4px',
            marginBottom: '-8px',
          }}
        >
          <IconButton ref={closeButtonRef} autoFocus onClick={onClose} aria-label="Close preview" sx={closeButtonSx}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              id="media-quick-preview-title"
              sx={{
                fontFamily: 'Rubik',
                fontWeight: 600,
                color: '#08231B',
                fontSize: { xs: '11px', md: '13px', lg: '15px', xl: '20px' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                [MOBILE]: { fontSize: '15px' },
              }}
            >
              {removeExtension(file?.name)}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                color: '#4f635d',
                fontSize: { xs: '9px', md: '10px', lg: '12px', xl: '16px' },
                [MOBILE]: { fontSize: '12px' },
              }}
            >
              {`${sizeConverter(parseInt(file?.size))} · ${timeStampToNumberedDateString(file?.upload_time)}${
                file?.uploaded_by ? ` · ${file.uploaded_by}` : ''
              }`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} data-testid="quick-preview-actions">
            {canRemove &&
              (isRemoving ? (
                <CircularProgress size={18} sx={{ m: 1, color: '#666' }} />
              ) : (
                <CustomTooltip title={removeMode === 'unlink' ? 'Remove this file' : 'Delete'}>
                  <span>
                    <IconButton
                      aria-label={`${removeMode === 'unlink' ? 'Remove' : 'Delete'} ${file?.name}`}
                      disabled={actionsDisabled}
                      onClick={() => setConfirmingRemove(true)}
                      sx={actionButtonSx}
                    >
                      {removeMode === 'unlink' ? <LinkOffIcon /> : <DeleteIcon />}
                    </IconButton>
                  </span>
                </CustomTooltip>
              ))}

            {canFavorite &&
              (isFavoriting ? (
                <CircularProgress size={18} sx={{ m: 1, color: '#666' }} />
              ) : (
                // A checkbox rather than a button, because it carries a state
                // the user is toggling rather than an action they are firing -
                // but it gets the same tooltip as its neighbours so it reads as
                // one of the row of actions it sits in.
                <CustomTooltip title={file?.favourite ? 'Unfavorite' : 'Favorite'}>
                  <span>
                    <Checkbox
                      sx={actionButtonSx}
                      onChange={(e) => onFavorite && onFavorite(file, e)}
                      checked={!!file?.favourite}
                      disabled={actionsDisabled}
                      icon={<StarOutlineIcon />}
                      checkedIcon={<StarIcon />}
                      inputProps={{ 'aria-label': `${file?.favourite ? 'Unfavorite' : 'Favorite'} ${file?.name}` }}
                    />
                  </span>
                </CustomTooltip>
              ))}

            {canDownload && (
              <CustomTooltip title="Download">
                <span>
                  <IconButton
                    aria-label={`Download ${file?.name}`}
                    disabled={actionsDisabled}
                    onClick={() => onDownload && onDownload(file)}
                    sx={actionButtonSx}
                  >
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}

          </Box>
        </Box>

        <Box sx={stageSx} data-testid="quick-preview-stage">
          {renderMedia()}

          {files.length > 1 && (
            <>
              <IconButton
                onClick={() => goTo(index - 1)}
                disabled={!hasPrevious}
                aria-label="Previous file"
                sx={{ ...navButtonSx, position: 'absolute', left: 8 }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => goTo(index + 1)}
                disabled={!hasNext}
                aria-label="Next file"
                sx={{ ...navButtonSx, position: 'absolute', right: 8 }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
        </Box>

        {files.length > 1 && (
          <Typography
            sx={{
              fontFamily: 'Rubik',
              color: '#4f635d',
              textAlign: 'center',
              fontSize: { xs: '9px', md: '10px', lg: '12px', xl: '16px' },
              [MOBILE]: { fontSize: '12px' },
            }}
          >
            {`${index + 1} of ${files.length}`}
          </Typography>
        )}
      </DialogContent>
    </Dialog>

    {/* Deliberately a second dialog rather than an inline swap of the button:
        the question has to be read before it is answered, and a control that
        changes meaning under the pointer is the thing that produced the
        mis-click in the first place. The wording is the bulk delete's, because
        deleting one file is no less permanent than deleting three. */}
    {confirmingRemove && <CustomAlert
      open
      handleClose={() => setConfirmingRemove(false)}
      data-testid="media-quick-preview-confirm"
      width={{ xs: '400px', sm: '400px', md: '420px', lg: '440px', xl: '600px' }}
      height={{ xs: '240px', sm: '240px', md: '250px', lg: '250px', xl: '320px' }}
    >
      <Box p={5} className="flex col j-center" sx={{ height: '100%' }}>
        <Box mb={{ xs: 1, md: 2 }} mt={1}>
          <Typography
            id="media-quick-preview-confirm-title"
            variant="h4"
            sx={{ fontFamily: 'Rubik', color: 'black', fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '13px', xl: '18px' } }}
          >
            {removeMode === 'unlink'
              ? 'Remove this file from this project?'
              : 'Are you sure you want to delete this file?'}
          </Typography>

          {/* The name on its own line rather than inside the question: a long
              filename in mid-sentence pushes the question onto three lines and
              buries what is being asked. */}
          <Typography
            data-testid="media-quick-preview-confirm-filename"
            sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', mt: 1, wordBreak: 'break-word', fontSize: { xs: '10px', md: '11px', lg: '12px', xl: '16px' } }}
          >
            {removeExtension(file?.name)}
          </Typography>

          <Typography
            sx={{ fontFamily: 'Rubik', fontStyle: 'italic', color: '#08231B', opacity: 0.75, mt: 0.6, fontSize: { xs: '9px', md: '10px', lg: '10px', xl: '13px' } }}
          >
            {removeMode === 'unlink'
              ? 'The file stays in the project it was imported from.'
              : 'This is permanent. Deleted files cannot be recovered.'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'end' }} gap={2} mt={{ xs: 2, md: 2.5, lg: 3 }}>
          {/* Both buttons state their own colours rather than taking MUI's
              `color="button"`. That palette key is this app's own, and this
              component is rendered - and tested - outside the custom theme, so
              resolving against it would throw there. The result is identical. */}
          <Button
            variant="outlined"
            data-testid="media-quick-preview-cancel-remove"
            onClick={() => setConfirmingRemove(false)}
            style={{ borderRadius: '5px', color: accent, borderColor: '#08231B', textTransform: 'none' }}
            sx={confirmButtonSx}
          >
            Cancel
          </Button>

          <LoadingButton
            autoFocus
            data-testid="media-quick-preview-confirm-remove"
            onClick={() => {
              setConfirmingRemove(false);
              if (onRemove) onRemove(file);
            }}
            // The accent is contrast-checked against this exact background, so
            // the pair cannot come out dark-on-dark the way an inherited
            // `contrastText` did.
            style={{ borderRadius: '5px', backgroundColor: '#08231B', color: accent, textTransform: 'none' }}
            sx={confirmButtonSx}
          >
            {removing}
          </LoadingButton>
        </Box>
      </Box>
    </CustomAlert>}
    </>
  );
};

export default MediaQuickPreview;
